import React from "react";
import SectionTitle from "../common/SectionTitle";
import SubHeading from "../common/SubHeading";
import { useFormSection, FormSectionProps } from "../../hooks/useFormSection";
import { drinkItems } from "../../lib/data/dietaryData";

const yesNoOptions = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const mealOptions = [
  { label: "0-1", value: "0" },
  { label: "1-3", value: "1" },
  { label: "3-5", value: "2" },
  { label: ">5 meals/week", value: "3" },
];

const dietHistoryOptions = [
  {
    value: "1",
    label: "I can eat practically anything I want and I don't gain weight.",
  },
  { value: "2", label: "I find it very hard to gain weight." },
  {
    value: "3",
    label:
      "I can lose or gain weight by adjusting my activity level and eating habits.",
  },
  { value: "4", label: "I find it difficult to lose weight." },
  {
    value: "5",
    label: "I can gain weight easily and have to watch what I eat.",
  },
];

const DietaryProfileSectionV2: React.FC<FormSectionProps> = (props) => {
  const { Field, formData, isEditMode = true } = useFormSection(props);
  
  const dietaryData = formData?.dietaryProfile || {};

  return (
    <section>
      <SectionTitle>Dietary Profile</SectionTitle>

      <Field
        path="dietaryProfile.foodsToNotEat"
        label="Please list foods that you will not eat under any circumstances:"
        type="textarea"
        rows={3}
      />
      
      <Field
        path="dietaryProfile.favoriteFoods"
        label="What are your favorite foods?"
        type="textarea"
        rows={3}
      />
      
      <Field
        path="dietaryProfile.perfectDayOfFood"
        label="What would a perfect day on your plate look like?"
        subLabel='(not healthiest, or what you think is "right". A day of food that would make you feel good physically and mentally. Include drinks, restaurants, sweet treats, and time stamps etc.)'
        type="textarea"
        rows={4}
      />
      
      <Field
        path="dietaryProfile.symptomsIfMealMissed"
        label="Do you experience any symptoms if meals are missed? Please explain"
        type="textarea"
        rows={3}
      />
      
      <Field
        path="dietaryProfile.symptomsAfterMeals"
        label="Do you experience any symptoms after meals? (bloating, gas, fatigue etc)"
        type="textarea"
        rows={3}
      />
      
      <Field
        path="dietaryProfile.foodsAvoided"
        label="Are there foods you avoid because of how they make you feel? Include the food & symptoms"
        type="textarea"
        rows={3}
      />
      
      <Field
        path="dietaryProfile.specialDiet"
        label="Do you currently follow a special diet or nutritional program? (vegan, low carb, high protein etc.)"
        type="textarea"
        rows={3}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
        <div>
          <Field
            path="dietaryProfile.groceryShop"
            label="Do you grocery shop?"
            type="radio"
            options={yesNoOptions}
          />
          {dietaryData.groceryShop === "no" && (
            <Field
              path="dietaryProfile.whoGroceryShops"
              type="text"
              placeholder="If not, who does?"
              render={({ value, onChange }) => (
                <input
                  type="text"
                  placeholder="If not, who does?"
                  value={value || ""}
                  onChange={(e) => onChange(e.target.value)}
                  className="w-full mt-2 p-2 border rounded bg-white"
                  disabled={!isEditMode}
                />
              )}
            />
          )}
        </div>
        
        <Field
          path="dietaryProfile.mealPlan"
          label="Do you meal plan or prep?"
          type="radio"
          options={yesNoOptions}
        />
        
        <div>
          <Field
            path="dietaryProfile.cook"
            label="Do you cook?"
            type="radio"
            options={yesNoOptions}
          />
          <Field
            path="dietaryProfile.enjoyCooking"
            type="text"
            placeholder="If yes do you enjoy it? If no, who does cook?"
            render={({ value, onChange }) => (
              <input
                type="text"
                placeholder="If yes do you enjoy it? If no, who does cook?"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full mt-2 p-2 border rounded bg-white"
                disabled={!isEditMode}
              />
            )}
          />
        </div>
        
        <Field
          path="dietaryProfile.readLabels"
          label="Do you read food labels?"
          type="radio"
          options={yesNoOptions}
        />
        
        <Field
          path="dietaryProfile.countCalories"
          label="Do you count calories?"
          type="radio"
          options={yesNoOptions}
        />
        
        <Field
          path="dietaryProfile.appetiteChange"
          label="Does your appetite change frequently?"
          type="textarea"
          rows={2}
        />
        
        <Field
          path="dietaryProfile.mealsOutPerWeek"
          label="How many meals do you eat out per week?"
          type="radio"
          options={mealOptions}
        />
        
        <div>
          <Field
            path="dietaryProfile.healthEatingChanges"
            label="Have you made any changes in your eating habits because of your health?"
            type="radio"
            options={yesNoOptions}
          />
          {dietaryData.healthEatingChanges === "yes" && (
            <Field
              path="dietaryProfile.healthEatingChangesDetails"
              type="text"
              placeholder="If yes, explain:"
              render={({ value, onChange }) => (
                <input
                  type="text"
                  placeholder="If yes, explain:"
                  value={value || ""}
                  onChange={(e) => onChange(e.target.value)}
                  className="w-full mt-2 p-2 border rounded bg-white"
                  disabled={!isEditMode}
                />
              )}
            />
          )}
        </div>
      </div>

      <SubHeading>
        How many single servings of the following do you drink per day?
      </SubHeading>
      <p className="text-sm text-brand-stone-500 mb-4">
        Please specify the type/brand when possible
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
        {drinkItems.map((item) => (
          <Field
            key={item}
            path={`dietaryProfile.servings.${item}`}
            label={item}
            type="text"
            placeholder="e.g., 2 cups Starbucks"
          />
        ))}
      </div>

      <SubHeading>Diet History</SubHeading>
      
      <Field
        path="dietaryProfile.dietHistoryDescription"
        label="Which of the following statements best describes you? Check one"
        type="radio"
        options={dietHistoryOptions}
      />
      
      <Field
        path="dietaryProfile.pastNutritionalProgram"
        label="Have you ever been placed on any type of nutritional program in the past?"
        type="radio"
        options={yesNoOptions}
      />
      
      {dietaryData.pastNutritionalProgram === "yes" && (
        <Field
          path="dietaryProfile.pastNutritionalProgramDetails"
          label="If yes, by whom and what did it consist of? Please explain below."
          type="textarea"
          rows={3}
        />
      )}
      
      <Field
        path="dietaryProfile.pastProgramResults"
        label="What were your results?"
        type="textarea"
        rows={3}
      />
      
      <Field
        path="dietaryProfile.pastProgramChallenges"
        label="What were the biggest challenges?"
        type="textarea"
        rows={3}
      />
    </section>
  );
};

export default DietaryProfileSectionV2;